import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The third written page, and the first with photography *inside* the story.
// Everything below the header is transcribed from the full-page capture of
// digitopia.co/customers/ebebek/ sent on 2026-08-28 — "bire bir aynı olacak
// şekilde" — and the three photographs on it came with it. The closing quote
// is not repeated as a `section`: `content/testimonials.ts` already holds
// Barış Şimşek's words keyed to this slug, and `StoryBody` draws that card.
// Reading them off the capture is in fact what confirmed the transcription —
// the four quoted paragraphs came back character for character identical to
// the ones already in the catalogue.
export const added = story('ebebek', 'ebebek', {
  doc: '665fc83c-01bc-4167-9ecc-ed59895e2bcb-Ebebek and Digitopia.pdf',
  video: '3tvGj1hxKR8',
  lede: [
    'ebebek started on a digital transformation journey with Digitopia to assess its digital '
    + 'maturity and implement a structured roadmap for continuous improvement.',
  ],
  intro: [
    'ebebek, Turkey’s leading mother and baby retailer, has been at the forefront of '
    + 'innovation in omnichannel retailing. With its unique click-to-brick model, ebebek first '
    + 'established itself as an e-commerce pioneer before expanding into physical retail. '
    + 'Knowing the rapid evolution of digital commerce and shifting consumer behaviors, ebebek '
    + 'started on a digital transformation journey with Digitopia to assess its digital '
    + 'maturity and implement a structured roadmap for continuous improvement under the '
    + 'stewardship of its new CTO.',
    'Recognizing the pivotal role of digital maturity in driving profitable growth and global '
    + 'expansion, the CTO leveraged Digitopia’s Digital Maturity Index (DMI) to obtain a '
    + 'comprehensive snapshot of the company’s digital landscape. This strategic move was '
    + 'aimed at charting a future-proof path for ebebek, setting a clear baseline to measure '
    + 'progress and impact over the years.',
  ],
  // The wide picture between the standfirst and the first heading, which is
  // where the capture runs it — the fourth of the four photographs that came
  // with the page (2026-08-28). The three inside the story landed with the
  // first pass and this one did not, so the page ran the standfirst straight
  // into "About ebebek" where the reference breaks.
  photo: {
    src: '/photo/ebebek-mother-and-baby-with-phone.webp',
    alt: 'A mother at her desk holding her baby on her lap, reading something on her phone, '
      + 'a laptop open beside her.',
  },
  sections: [
    {
      heading: 'About ebebek: A 360° Mother & Baby Ecosystem',
      centred: true,
      body: [
        'Founded in 2001, ebebek has grown into Turkey’s largest and most trusted baby retail '
        + 'brand, offering a seamless omnichannel experience through its extensive network of '
        + 'over 270 stores in 67 cities and a powerful e-commerce platform. With a strong '
        + 'emphasis on customer experience, product quality, and technological innovation, '
        + 'ebebek is committed to meeting the evolving needs of parents worldwide. As part of '
        + 'its “2030 – 6 Continents” vision, ebebek is expanding its global footprint through '
        + 'partnerships with leading online marketplaces like Amazon and AliExpress.',
      ],
    },
    {
      heading: 'The Challenge: Scaling Digital Maturity for Sustainable Growth',
      centred: true,
      body: [
        'Despite being inherently digital at its core, ebebek faced challenges in maintaining a '
        + 'structured and scalable digital transformation approach. Upon his arrival, the new '
        + 'CTO faced the immediate challenge of understanding the digital health of ebebek '
        + 'comprehensively. With ambitious goals for global expansion and the need to maintain '
        + 'a competitive edge in the dynamic e-commerce landscape, it was essential to assess '
        + 'the current state of digital operations, identify areas for improvement, and '
        + 'strategize for long-term success.',
      ],
      photo: {
        src: '/photo/ebebek-store-exterior.webp',
        alt: 'An ebebek store, its blue façade carrying the brand’s wordmark and the line '
          + '“Anne ilgisi, uzman bilgisi” above the shopfront.',
      },
    },
    {
      heading: 'Why ebebek Chose Digitopia: Nurturing Digital Growth with a Strategic Vision',
      band: true,
      centred: true,
      body: [
        'ebebek selected Digitopia’s Digital Maturity Index (DMI) to gain a comprehensive '
        + 'understanding of its digital capabilities across six key dimensions: technology, '
        + 'governance, people, processes, operations, and customer experience. This structured '
        + 'assessment provided ebebek with:',
      ],
      items: [
        {
          lead: 'Benchmarking Insights',
          text: 'Comparing its digital standing with industry peers.',
        },
        {
          lead: 'A Clear Roadmap',
          text: 'Prioritizing key initiatives based on data-driven insights.',
        },
        {
          lead: 'Periodic Evaluations',
          text: 'Ensuring long-term alignment with digital transformation goals.',
        },
      ],
    },
    {
      heading: 'The Approach: A Holistic Digital Roadmap',
      band: true,
      photo: {
        src: '/photo/ebebek-mother-and-sleeping-baby.webp',
        alt: 'A mother holding her sleeping baby, wrapped in a blue sling against her shoulder.',
      },
      photoSide: 'left',
      body: [
        'With Digitopia’s support, ebebek developed a structured, action-oriented '
        + 'transformation strategy focusing on three key pillars:',
      ],
      items: [
        {
          lead: 'Building a Robust IT Infrastructure',
          text: 'Enhancing reliability and scalability to support long-term growth.',
        },
        {
          lead: 'Leveraging Smart Technologies',
          text: 'Implementing AI-driven personalization, automation, and predictive analytics '
            + 'to improve customer experience.',
        },
        {
          lead: 'Investing in Talent Development',
          text: 'Upskilling teams to drive digital transformation from within.',
        },
      ],
      after: [
        'Digitopia’s DMI solution offered a structured approach to tackle these challenges '
        + 'head-on. By evaluating ebebek’s digital capabilities across six key dimensions, '
        + 'Digitopia provided a holistic view of the company’s digital maturity, enabling the '
        + 'CTO to make informed decisions and set a strategic roadmap for digital excellence.',
      ],
    },
    {
      heading: 'The Results: A More Resilient & Digital-First Organization',
      band: true,
      photo: {
        src: '/photo/ebebek-baby-in-bubbles.webp',
        alt: 'A laughing baby sitting in a bath, surrounded by soap bubbles.',
      },
      photoSide: 'right',
      body: ['Through its collaboration with Digitopia, ebebek has achieved:'],
      items: [
        {
          lead: 'Team Alignment',
          text: 'Ensuring all business units contribute to digital initiatives.',
        },
        {
          lead: 'Enhanced Digital Maturity',
          text: 'The company has made substantial progress in its digital capabilities, '
            + 'positioning itself as a leader in the competitive e-commerce space.',
        },
        {
          lead: 'Strategic Clarity and Direction',
          text: 'The DMI scores and analysis provided a clear roadmap for digital '
            + 'transformation, aligning the entire organization around shared goals and '
            + 'objectives.',
        },
        {
          lead: 'Profitable Growth and Global Expansion',
          text: 'ebebek’s focused approach to digital maturity has been a key driver of its '
            + 'success, enabling the company to scale internationally while maintaining '
            + 'profitability.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a full-page screenshot of '
    + `${STORY_SOURCE}/ebebek/ supplied on 2026-08-28, not from a file we hold — the capture is `
    + '2326px wide over 11696px tall, so the body type in it is a couple of pixels high. Two '
    + 'reassurances and one job. The reassurances: the four paragraphs of Barış Şimşek’s quote '
    + 'read off this capture match the ones already in content/testimonials.ts word for word, '
    + 'and the shared pillars under “The Approach” match Petrol Ofisi’s page, which is the same '
    + 'boilerplate — so the transcription is not guesswork. The job: proofread the figures '
    + 'against the source before production — “Founded in 2001”, “over 270 stores in 67 '
    + 'cities”, the “2030 – 6 Continents” vision, and the two marketplaces named after it '
    + '(Amazon and AliExpress). Also worth a decision: the header eyebrow reads “Digitopia / '
    + 'Customer Success Story” here and “SUCCESS STORIES” on digitopia.co. It was left alone '
    + 'because it is one constant shared by all twenty-two pages, not this page’s own line.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 11
